import RefundCollectionSummary from '../components/RefundCollectionSummary';
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const PERIODS = [
  { key:'current_month', label:'This Month' },
  { key:'last_month',    label:'Last Month' },
  { key:'last_3_months', label:'Last 3 Months' },
  { key:'last_6_months', label:'Last 6 Months' },
  { key:'last_year',     label:'This Year' },
  { key:'all_time',      label:'All Time' },
];

const Badge = ({ status }) => (
  <span className={'badge b-' + (status==='paid'?'paid':status==='part_paid'?'part':'unpaid')}>
    {(status||'').replace('_',' ')}
  </span>
);

export default function Dashboard() {
  const { hasPermission, user } = useAuth();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [unreadReports, setUnreadReports] = useState(0);
  const [pendingRefunds, setPendingRefunds] = useState(0);
  const [unreadSops, setUnreadSops] = useState(0);
  const [pendingExpenses, setPendingExpenses] = useState(0);
  const [pendingAdjustments, setPendingAdjustments] = useState(0);
  const [pendingPayroll, setPendingPayroll] = useState(0);
  const [pendingDiscounts, setPendingDiscounts] = useState(0);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [pendingDispatch, setPendingDispatch] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('current_month');
  const [showHR, setShowHR] = useState(() => {
    try { return localStorage.getItem('bloans_dashboard_view') === 'hr'; } catch(e) { return false; }
  });
  const toggleHR = (val) => {
    setShowHR(val);
    try { localStorage.setItem('bloans_dashboard_view', val ? 'hr' : 'main'); } catch(e) {}
  };

  const load = (p) => {
    setLoading(true);
    get('/dashboard?period='+p).then(setData).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(period); }, [period]);

  useEffect(() => {
    if (['super_admin','admin'].includes(user?.role)) {
      get('/team-reports/unread-count').then(d => setUnreadReports(d.count||0)).catch(()=>{});
    }
    if (user?.role === 'super_admin') {
      get('/refunds/pending').then(d => setPendingRefunds((d.data||[]).length)).catch(()=>{});
    }
    get('/sops/unread-count').then(d => setUnreadSops(d.count||0)).catch(()=>{});
    if (['super_admin','admin'].includes(user?.role)) {
      get('/expenses/pending').then(d => setPendingExpenses((d.data||[]).length)).catch(()=>{});
    }
    if (user?.role === 'super_admin') {
      get('/inventory/adjustment-requests/count').then(d => setPendingAdjustments(d.count||0)).catch(()=>{});
      get('/payroll/entries/pending').then(d => setPendingPayroll((d.data||[]).length)).catch(()=>{});
      get('/invoices/pending-discounts').then(d => setPendingDiscounts((d.data||[]).length)).catch(()=>{});
      get('/leave/pending-count').then(d => setPendingLeave(d.count||0)).catch(()=>{});
      get('/job-orders/dispatch-overrides/pending').then(d => setPendingDispatch(d.count||0)).catch(()=>{});
    }
  }, []);

  useEffect(() => {
    get('/tasks?status=pending').then(d => {
      const t = d.data||[];
      // Show only tasks due today or overdue
      const urgent = t.filter(task => {
        if (!task.due_date) return false;
        return new Date(task.due_date) <= new Date(new Date().toDateString());
      });
      setTasks(urgent);
    }).catch(()=>{});
  }, []);

  // PRODUCTION FINANCIAL DASHBOARD REDIRECT
  if (
    [
      'production_team_lead',
      'inventory_officer',
      'production_supervisor',
      'production_assistant',
      'production_staff',
      'tailor'
    ].includes(user?.role)
  ) {
    return (
      <Navigate
        to="/production-dashboard"
        replace
      />
    );
  }

  if (!hasPermission('dashboard:read')) {
    return (
      <div className="empty-state">
        <h3>Access Restricted</h3>
        <p>You do not have permission to view the dashboard.</p>
      </div>
    );
  }

  if (loading) return <div className="spinner" style={{marginTop:40}}></div>;

  // Show sales manager dashboard if role is sales_manager
  if (data && data.role === 'sales_manager') {
    return <SalesManagerDashboard data={data} period={period} setPeriod={setPeriod} tasks={tasks} />;
  }
  if (user?.role === 'hr_officer' || showHR) {
    return (
      <div>
        {user?.role === 'super_admin' && (
          <button onClick={()=>toggleHR(true)}
            style={{fontSize:12,padding:'6px 14px',borderRadius:6,border:'1px solid #556B2F',
              background:'#fff',cursor:'pointer',color:'#556B2F',marginRight:8}}>
            👥 HR View
          </button>
        )}
        {user?.role === 'super_admin' && (
          <div style={{marginBottom:16,textAlign:'right'}}>
            <button onClick={()=>setShowHR(false)}
              style={{fontSize:12,padding:'6px 14px',borderRadius:6,border:'1px solid #E8E0D4',
                background:'#fff',cursor:'pointer',color:'#556B2F'}}>
              ← Back to Main Dashboard
            </button>
          </div>
        )}
        <HRErrorBoundary><HRDashboard /></HRErrorBoundary>
      </div>
    );
  }

  if (!data) return <div className='empty-state'><h3>Unable to load dashboard</h3></div>;

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',color:'var(--forest)'}}>
            Good {new Date().getHours()<12?'morning':new Date().getHours()<17?'afternoon':'evening'}
          </h2>
          <p className="text-muted text-sm">
            {new Date().toLocaleDateString('en-NG',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
          </p>
        </div>
        {/* Period selector */}
        <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end'}}>
          {PERIODS.map(p => (
            <button key={p.key}
              className={'btn btn-sm '+(period===p.key?'btn-primary':'btn-outline')}
              onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="spinner"></div> : !data ? (
        <div className="alert alert-error">Failed to load dashboard.</div>
      ) : (
        <>
          {/* Period label */}
          <div style={{marginBottom:16,padding:'8px 14px',background:'var(--cream)',borderRadius:8,
            display:'inline-flex',alignItems:'center',gap:8,fontSize:13,color:'var(--forest)',fontWeight:500}}>
            Showing: <strong>{data.period_label}</strong>
          </div>

          <RefundCollectionSummary collections={data.customer_collections} unallocated={data.unallocated_refunds} />

          {/* Metric tiles */}
          <div className="metrics">
            {user?.role === 'super_admin' && (
              <div className="tile">
                <div className="tile-lbl">{data.period_label} Revenue</div>
                <div className="tile-val pos">{fmt(data.summary.period_revenue)}</div>
              </div>
            )}
            {['super_admin','admin'].includes(user?.role) && (
              <div className="tile">
                <div className="tile-lbl">{data.period_label} Expenses</div>
                <div className="tile-val warn">{fmt(data.summary.period_expenses)}</div>
              </div>
            )}
            {user?.role === 'super_admin' && (
              <div className="tile">
                <div className="tile-lbl">{data.period_label} Profit</div>
                <div className={'tile-val '+(data.summary.period_profit>=0?'pos':'neg')}>
                  {fmt(data.summary.period_profit)}
                </div>
              </div>
            )}
            <div className="tile">
              <div className="tile-lbl">Outstanding Receivables</div>
              <div className="tile-val warn">{fmt(data.summary.outstanding_receivables)}</div>
            </div>
            <div className="tile">
              <div className="tile-lbl">Cash on Hand</div>
              <div className="tile-val pos">{fmt(data.summary.cash_balance)}</div>
            </div>
            <div className="tile">
              <div className="tile-lbl">Bank Balance</div>
              <div className="tile-val pos">{fmt(data.summary.bank_balance)}</div>
            </div>
            <div className="tile">
              <div className="tile-lbl">Outstanding Payables</div>
              <div className="tile-val">{fmt(data.summary.outstanding_payables)}</div>
            </div>
            <div className="tile">
              <div className="tile-lbl">Inventory Value</div>
              <div className="tile-val">{fmt(data.summary.inventory_value)}</div>
            </div>
          </div>

          {/* Low stock alert */}
          {data.alerts.low_stock_materials.length > 0 && (
            <div className="alert alert-warn mb-lg">
              Low stock: <strong>{data.alerts.low_stock_materials.map(m=>m.name).join(', ')}</strong>
            </div>
          )}

          {/* Chart + Recent invoices */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            <div className="card">
              <div className="card-hdr">
                <span className="card-title">Revenue vs Expenses</span>
              </div>
              <div style={{padding:16}}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.monthly_trend}>
                    <XAxis dataKey="month" tick={{fontSize:11}} />
                    <YAxis tick={{fontSize:11}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                    <Tooltip formatter={v=>fmt(v)} />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenue" fill="#556B2F" radius={[3,3,0,0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="#A8893D" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card">
              <div className="card-hdr">
                <span className="card-title">Recent Invoices — {data.period_label}</span>
              </div>
              {data.recent_invoices.length === 0
                ? <div className="empty-state" style={{padding:24}}><p>No invoices for this period</p></div>
                : data.recent_invoices.map(inv => (
                  <div key={inv.invoice_number} style={{display:'flex',alignItems:'center',
                    justifyContent:'space-between',padding:'11px 20px',borderBottom:'1px solid var(--border-light)'}}>
                    <div>
                      <div style={{fontWeight:500,fontSize:13}}>{inv.customer_name}</div>
                      <div className="text-muted text-xs">{inv.invoice_number} · {inv.date}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div className="amt" style={{fontSize:13}}>{fmt(inv.total)}</div>
                      <Badge status={inv.status} />
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Unread reports banner */}
          {unreadReports > 0 && (
            <div style={{marginTop:16,padding:'12px 20px',background:'#EDF4FF',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #C3D9FF'}}>
              <div style={{fontSize:13,color:'#1E4FAD',fontWeight:500}}>
                📋 You have <strong>{unreadReports}</strong> unread team report{unreadReports!==1?'s':''}
              </div>
              <a href="/team-reports" style={{fontSize:12,color:'#1E4FAD',fontWeight:600,textDecoration:'none'}}>View Reports →</a>
            </div>
          )}

          {/* Pending refunds banner */}
          {pendingRefunds > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#FDEAEA',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #F5B8B8'}}>
              <div style={{fontSize:13,color:'#8F2E24',fontWeight:500}}>
                💰 You have <strong>{pendingRefunds}</strong> pending refund request{pendingRefunds!==1?'s':''} awaiting approval
              </div>
              <a href="/refunds-pending" style={{fontSize:12,color:'#8F2E24',fontWeight:600,textDecoration:'none'}}>Review Refunds →</a>
            </div>
          )}

          {/* Pending expenses banner */}
          {pendingExpenses > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#FFF3DC',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #F5C842'}}>
              <div style={{fontSize:13,color:'#8B5E0A',fontWeight:500}}>
                💰 <strong>{pendingExpenses}</strong> expense{pendingExpenses!==1?'s':''} above ₦10,000 awaiting approval
              </div>
              <a href="/expenses" style={{fontSize:12,color:'#8B5E0A',fontWeight:600,textDecoration:'none'}}>Review →</a>
            </div>
          )}

          {/* Pending inventory adjustments banner */}
          {pendingAdjustments > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#EDF4FF',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #C3D9FF'}}>
              <div style={{fontSize:13,color:'#1E4FAD',fontWeight:500}}>
                📦 <strong>{pendingAdjustments}</strong> inventory adjustment{pendingAdjustments!==1?'s':''} awaiting your approval
              </div>
              <a href="/inventory" style={{fontSize:12,color:'#1E4FAD',fontWeight:600,textDecoration:'none'}}>Review →</a>
            </div>
          )}

          {/* Pending discounts banner */}
          {pendingDiscounts > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#FFF3DC',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #F5C842'}}>
              <div style={{fontSize:13,color:'#8B5E0A',fontWeight:500}}>
                🏷 <strong>{pendingDiscounts}</strong> invoice discount{pendingDiscounts!==1?'s':''} awaiting approval
              </div>
              <a href="/invoices" style={{fontSize:12,color:'#8B5E0A',fontWeight:600,textDecoration:'none'}}>Review →</a>
            </div>
          )}

          {/* Pending dispatch override banner */}
          {pendingDispatch > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#FDEAEA',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #F5B8B8'}}>
              <div style={{fontSize:13,color:'#8F2E24',fontWeight:500}}>
                🚚 <strong>{pendingDispatch}</strong> dispatch override{pendingDispatch!==1?'s':''} awaiting your approval
              </div>
              <a href="/job-orders" style={{fontSize:12,color:'#8F2E24',fontWeight:600,textDecoration:'none'}}>Review →</a>
            </div>
          )}

          {/* Pending leave banner */}
          {pendingLeave > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#E8F5EB',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #B8DEC0'}}>
              <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>
                🏖 <strong>{pendingLeave}</strong> leave request{pendingLeave!==1?'s':''} awaiting approval
              </div>
              <a href="/leave" style={{fontSize:12,color:'#2E6B3A',fontWeight:600,textDecoration:'none'}}>Review →</a>
            </div>
          )}

          {/* Pending payroll banner */}
          {pendingPayroll > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#F0E6FF',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #D4B8FF'}}>
              <div style={{fontSize:13,color:'#6B2FAD',fontWeight:500}}>
                💼 <strong>{pendingPayroll}</strong> payroll entr{pendingPayroll!==1?'ies':'y'} awaiting your approval
              </div>
              <a href="/payroll" style={{fontSize:12,color:'#6B2FAD',fontWeight:600,textDecoration:'none'}}>Review →</a>
            </div>
          )}

          {/* Unread SOPs banner */}
          {unreadSops > 0 && (
            <div style={{marginTop:8,padding:'12px 20px',background:'#FFF3DC',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',border:'1px solid #F5C842'}}>
              <div style={{fontSize:13,color:'#8B5E0A',fontWeight:500}}>
                📋 <strong>{unreadSops}</strong> SOP/Policy update{unreadSops!==1?'s':''} require your confirmation
              </div>
              <a href="/sops" style={{fontSize:12,color:'#8B5E0A',fontWeight:600,textDecoration:'none'}}>View & Confirm →</a>
            </div>
          )}

          {/* Team Performance — shown in SalesManagerDashboard only */}
      )}
      {/* Tasks widget */}
      {tasks.length > 0 && (
        <div className="card" style={{marginTop:16}}>
          <div className="card-hdr">
            <span className="card-title" style={{color:'var(--unpaid)'}}>⚠ My Tasks Due / Overdue ({tasks.length})</span>
            <a href="/tasks" style={{fontSize:12,color:'var(--forest)',fontWeight:500,textDecoration:'none'}}>View All →</a>
          </div>
          {tasks.slice(0,3).map(t => (
            <div key={t.id} style={{padding:'10px 20px',borderBottom:'1px solid var(--border-light)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontWeight:500,fontSize:13}}>{t.title}</div>
              <div style={{fontSize:11,color:'var(--unpaid)',fontWeight:600}}>{t.due_date}</div>
            </div>
          ))}
        </div>
      )}

      {/* Recent payments */}
          {data.recent_payments.length > 0 && (
            <div className="card" style={{marginTop:16}}>
              <div className="card-hdr">
                <span className="card-title">Recent Payments — {data.period_label}</span>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{background:'var(--cream)'}}>
                    <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Ref</th>
                    <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Customer</th>
                    <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Invoice</th>
                    <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Method</th>
                    <th style={{padding:'9px 16px',textAlign:'right',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_payments.map(p => (
                    <tr key={p.payment_number}>
                      <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)'}}>{p.payment_number}</td>
                      <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',fontWeight:500}}>{p.customer_name}</td>
                      <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',color:'var(--ink-light)'}}>{p.invoice_number}</td>
                      <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)'}}><span className="badge b-grey">{p.method}</span></td>
                      <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600,color:'var(--paid)'}}>{fmt(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        {/* Tasks widget */}
          {tasks.length > 0 && (
            <div className="card" style={{marginTop:16}}>
              <div className="card-hdr">
                <span className="card-title" style={{color:'var(--unpaid)'}}>⚠ Tasks Due Today / Overdue ({tasks.length})</span>
                <a href="/tasks" style={{fontSize:12,color:'var(--forest)',fontWeight:500,textDecoration:'none'}}>View All →</a>
              </div>
              {tasks.slice(0,5).map(t => (
                <div key={t.id} style={{padding:'10px 20px',borderBottom:'1px solid var(--border-light)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontWeight:500,fontSize:13}}>{t.title}</div>
                    <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{t.category} {t.assigned_to_name?'→ '+t.assigned_to_name:''}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:11,color:'var(--unpaid)',fontWeight:600}}>{t.due_date}</div>
                    <span style={{fontSize:9,fontWeight:700,color:'var(--unpaid)',background:'#FDEAEA',padding:'2px 6px',borderRadius:20}}>
                      {new Date(t.due_date) < new Date(new Date().toDateString()) ? 'OVERDUE' : 'DUE TODAY'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SalesManagerDashboard({ data, period, setPeriod, tasks=[] }) {
  const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
  const summary = data?.summary || {};
  const recent_invoices = data?.recent_invoices || [];
  const recent_payments = data?.recent_payments || [];
  const top_customers = data?.top_customers || [];
  const period_label = data?.period_label || '';
  const team_stats = data?.team_stats || [];

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',color:'var(--forest)'}}>
            Good {new Date().getHours()<12?'morning':new Date().getHours()<17?'afternoon':'evening'}
          </h2>
          <p className="text-muted text-sm">
            {new Date().toLocaleDateString('en-NG',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
          </p>
        </div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end'}}>
          {PERIODS.map(p => (
            <button key={p.key}
              className={'btn btn-sm '+(period===p.key?'btn-primary':'btn-outline')}
              onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{marginBottom:16,padding:'8px 14px',background:'var(--cream)',borderRadius:8,
        display:'inline-flex',alignItems:'center',gap:8,fontSize:13,color:'var(--forest)',fontWeight:500}}>
        My Sales — <strong>{period_label}</strong>
      </div>

      <div className="metrics">
        <div className="tile">
          <div className="tile-lbl">My Sales {period_label}</div>
          <div className="tile-val pos">{fmt(summary.period_revenue)}</div>
          <div className="tile-sub">Total invoiced</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Collected {period_label}</div>
          <div className="tile-val pos">{fmt(summary.period_collected)}</div>
          <div className="tile-sub">Payments received</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Outstanding</div>
          <div className="tile-val warn">{fmt(summary.outstanding_receivables)}</div>
          <div className="tile-sub">Balance due from my invoices</div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginTop:16}}>
        {/* Recent invoices */}
        <div className="card">
          <div className="card-hdr"><span className="card-title">My Recent Invoices</span></div>
          {(recent_invoices||[]).length === 0
            ? <div className="empty-state" style={{padding:24}}><p>No invoices this period</p></div>
            : (recent_invoices||[]).map(inv => (
              <div key={inv.invoice_number} style={{display:'flex',alignItems:'center',
                justifyContent:'space-between',padding:'11px 20px',borderBottom:'1px solid var(--border-light)'}}>
                <div>
                  <div style={{fontWeight:500,fontSize:13}}>{inv.customer_name}</div>
                  <div className="text-muted text-xs">{inv.invoice_number} · {inv.date}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div className="amt" style={{fontSize:13}}>{fmt(inv.total)}</div>
                  <Badge status={inv.status} />
                </div>
              </div>
            ))
          }
        </div>

        {/* Top customers */}
        <div className="card">
          <div className="card-hdr"><span className="card-title">My Top Customers</span></div>
          {(top_customers||[]).length === 0
            ? <div className="empty-state" style={{padding:24}}><p>No customers this period</p></div>
            : (top_customers||[]).map(c => (
              <div key={c.name} style={{display:'flex',alignItems:'center',
                justifyContent:'space-between',padding:'11px 20px',borderBottom:'1px solid var(--border-light)'}}>
                <div>
                  <div style={{fontWeight:500,fontSize:13}}>{c.name}</div>
                  <div className="text-muted text-xs">{c.invoice_count} invoice{c.invoice_count!==1?'s':''}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div className="amt" style={{fontSize:13,color:'var(--paid)'}}>{fmt(c.total_paid)}</div>
                  <div className="text-muted text-xs">of {fmt(c.total_billed)}</div>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      {/* Team Performance */}
      {team_stats.length > 0 && (
        <div className="card" style={{marginTop:16}}>
          <div className="card-hdr">
            <span className="card-title">👥 Team Performance — {period_label}</span>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Team Member','Role','Invoices','Billed','Collected'].map(h=>(
                <th key={h} style={{padding:'8px 14px',color:'#F2EFE7',fontSize:11,fontWeight:600,textAlign:h==='Invoices'||h==='Billed'||h==='Collected'?'right':'left',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {team_stats.map((m,i)=>(
                <tr key={m.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                  <td style={{padding:'10px 14px',fontWeight:500}}>{m.name}</td>
                  <td style={{padding:'10px 14px',color:'#8A8A8A',fontSize:12}}>{m.role.replace(/_/g,' ')}</td>
                  <td style={{padding:'10px 14px',textAlign:'right'}}>{m.invoice_count}</td>
                  <td style={{padding:'10px 14px',textAlign:'right',color:'#556B2F',fontWeight:500}}>{fmt(m.total_billed)}</td>
                  <td style={{padding:'10px 14px',textAlign:'right',color:'#2E6B3A',fontWeight:600}}>{fmt(m.total_collected)}</td>
                </tr>
              ))}
              {team_stats.length === 0 && (
                <tr><td colSpan={5} style={{padding:20,textAlign:'center',color:'#8A8A8A'}}>No team activity this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Tasks widget */}
      {tasks.length > 0 && (
        <div className="card" style={{marginTop:16}}>
          <div className="card-hdr">
            <span className="card-title" style={{color:'var(--unpaid)'}}>⚠ My Tasks Due / Overdue ({tasks.length})</span>
            <a href="/tasks" style={{fontSize:12,color:'var(--forest)',fontWeight:500,textDecoration:'none'}}>View All →</a>
          </div>
          {tasks.slice(0,3).map(t => (
            <div key={t.id} style={{padding:'10px 20px',borderBottom:'1px solid var(--border-light)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontWeight:500,fontSize:13}}>{t.title}</div>
              <div style={{fontSize:11,color:'var(--unpaid)',fontWeight:600}}>{t.due_date}</div>
            </div>
          ))}
        </div>
      )}

      {/* Recent payments */}
      {(recent_payments||[]).length > 0 && (
        <div className="card" style={{marginTop:16}}>
          <div className="card-hdr"><span className="card-title">Recent Payments on My Invoices</span></div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{background:'var(--cream)'}}>
                <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Ref</th>
                <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Customer</th>
                <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Invoice</th>
                <th style={{padding:'9px 16px',textAlign:'right',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(recent_payments||[]).map(p => (
                <tr key={p.payment_number}>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)'}}>{p.payment_number}</td>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',fontWeight:500}}>{p.customer_name}</td>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',color:'var(--ink-light)'}}>{p.invoice_number}</td>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',textAlign:'right',
                    fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600,color:'var(--paid)'}}>{fmt(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

class HRErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding:24,background:'#FDEAEA',borderRadius:10,color:'#8F2E24'}}>
          <strong>HR Dashboard Error:</strong> {this.state.error?.message || 'Unknown error'}
          <br/><button onClick={()=>this.setState({hasError:false})} style={{marginTop:8,padding:'4px 12px',cursor:'pointer'}}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function HRDashboard() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const token = localStorage.getItem('bb_token');
    fetch('/api/dashboard/hr', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(r => r.json()).then(d => {
      if (d.error) setData(null);
      else setData(d);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fmt = v => v ?? 0;
  const fmtDate = v => v ? new Date(v).toLocaleDateString('en-NG', {day:'numeric',month:'short'}) : '—';

  const Tile = ({label, value, icon, color, sub}) => (
    <div style={{background:'#fff',borderRadius:12,padding:'18px 20px',boxShadow:'0 2px 8px rgba(0,0,0,.06)',
      borderLeft:'4px solid '+color}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontSize:11,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>{label}</div>
          <div style={{fontSize:28,fontWeight:700,color:'#2C1F16'}}>{value}</div>
          {sub && <div style={{fontSize:11,color:'#8A8A8A',marginTop:4}}>{sub}</div>}
        </div>
        <div style={{fontSize:28}}>{icon}</div>
      </div>
    </div>
  );

  if (loading) return <div className="spinner" style={{marginTop:40}}/>;
  if (!data) return <div className="empty-state"><h3>Unable to load HR dashboard</h3></div>;

  const { stats, upcoming_birthdays, recent_leave, recent_prospects } = data;

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',color:'var(--forest)'}}>
            HR Dashboard
          </h2>
          <p className="text-muted text-sm">Bloans Creations Ltd. — People & Culture Overview</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
        <Tile label="Total Staff" value={fmt(stats.total_staff)} icon="👥" color="#556B2F" />
        <Tile label="Pending Leave" value={fmt(stats.pending_leave)} icon="🏖️" color="#A8893D"
          sub={stats.pending_leave > 0 ? 'Needs approval' : 'All clear'} />
        <Tile label="Active Onboarding" value={fmt(stats.pending_onboarding)} icon="🎯" color="#3B2A21" />
        <Tile label="In Offboarding" value={fmt(stats.pending_offboarding)} icon="🚪" color="#8F2E24" />
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
        <Tile label="Clocked In Today" value={fmt(stats.attendance_today)} icon="⏰" color="#556B2F" />
        <Tile label="Bio-Data Missing" value={fmt(stats.biodata_missing)} icon="📋" color="#A8893D"
          sub="Staff yet to submit" />
        <Tile label="Not on Payroll" value={fmt(stats.payroll_not_enrolled)} icon="💳" color="#8F2E24"
          sub="Enrolment pending" />
        <Tile label="Active Staff" value={fmt(stats.active_staff)} icon="✅" color="#2E6B3A" />
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16}}>

        {/* Pending Leave */}
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:14}}>🏖️ Pending Leave Requests</div>
          {recent_leave.length === 0 ? (
            <p style={{fontSize:13,color:'#8A8A8A'}}>No pending requests</p>
          ) : recent_leave.map((lr,i) => (
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{fontWeight:600,color:'#2C1F16'}}>{lr.staff_name}</div>
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                {lr.leave_type} · {fmtDate(lr.start_date)} – {fmtDate(lr.end_date)}
              </div>
            </div>
          ))}
          {stats.pending_leave > 5 && (
            <a href="/leave" style={{fontSize:12,color:'#556B2F',marginTop:8,display:'block'}}>View all →</a>
          )}
        </div>

        {/* Upcoming Birthdays */}
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:14}}>🎂 Upcoming Birthdays</div>
          {upcoming_birthdays.length === 0 ? (
            <p style={{fontSize:13,color:'#8A8A8A'}}>No birthdays in the next 14 days</p>
          ) : upcoming_birthdays.map((b,i) => (
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{fontWeight:600,color:'#2C1F16'}}>{b.name}</div>
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                {new Date(b.date_of_birth).toLocaleDateString('en-NG',{day:'numeric',month:'long'})}
              </div>
            </div>
          ))}
        </div>

        {/* Recent Prospects */}
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:14}}>🎯 Recent Prospects</div>
          {recent_prospects.length === 0 ? (
            <p style={{fontSize:13,color:'#8A8A8A'}}>No prospects</p>
          ) : recent_prospects.map((p,i) => (
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{fontWeight:600,color:'#2C1F16'}}>{p.full_name}</div>
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                {p.position} · <span style={{
                  color: p.status==='offer_sent'?'#A8893D':p.status==='hired'?'#556B2F':'#8A8A8A'
                }}>{p.status?.replace(/_/g,' ')}</span>
              </div>
            </div>
          ))}
          <a href="/prospects" style={{fontSize:12,color:'#556B2F',marginTop:8,display:'block'}}>View all →</a>
        </div>

      </div>
    </div>
  );
}
